export type TopicAudience = "all" | "young" | "special";

export type TopicRef = {
  id: string;
  audience: TopicAudience;
};

export type SkillRef = {
  id: string;
  topics: readonly TopicRef[];
};

export type CompetencyRef = {
  id: string;
  skills: readonly SkillRef[];
};

const topic = (id: string, audience: TopicAudience = "all"): TopicRef => ({ id, audience });

/** Official order of the bekwaamheidseisen. Topics are the teacher's steps; criteria stay in copy. */
export const startingTeacherRoute: readonly CompetencyRef[] = [
  {
    id: "inhoudelijk",
    skills: [
      { id: "kennisVak", topics: [topic("methode"), topic("wereld"), topic("basis")] },
      { id: "kennisCurriculum", topics: [topic("kerndoelen"), topic("leerlijnen"), topic("opMaat")] },
    ],
  },
  {
    id: "didactisch",
    skills: [
      {
        id: "instructie",
        topics: [
          topic("les"),
          topic("verwachtingen"),
          topic("verschillen"),
          topic("middelen"),
          topic("jongeKind", "young"),
          topic("speciaal", "special"),
        ],
      },
      { id: "effect", topics: [topic("zien"), topic("bijsturen"), topic("eigenaarschap")] },
    ],
  },
  {
    id: "pedagogisch",
    skills: [
      { id: "relatie", topics: [topic("veiligeRelatie"), topic("sociaal"), topic("vastlopen")] },
      { id: "klassen", topics: [topic("regie"), topic("groep"), topic("routines")] },
    ],
  },
  {
    id: "professioneel",
    skills: [
      { id: "reflectie", topics: [topic("aanpak"), topic("hulp"), topic("visie")] },
      { id: "organisatie", topics: [topic("team"), topic("positie")] },
      { id: "omgeving", topics: [topic("ouders"), topic("zorg")] },
      { id: "beroep", topics: [topic("waarden"), topic("netwerk")] },
    ],
  },
];

/** First months: classroom routines, then the rest of year one, then subject and school. */
export const leidraadTopicIds = ["regie", "veiligeRelatie", "les", "hulp"] as const;

export const routeStart = {
  competencyId: "inhoudelijk",
  skillId: "kennisVak",
  topicId: "methode",
} as const;

/** Public E-WISE course page. The slug is the title; the host path stays on the course id. */
export function ewiseCourseUrl(courseId: string, title: string): string {
  const slug = title
    .replace(/\s*\([^)]*\)\s*$/g, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .split(/[^A-Za-z0-9]+/)
    .filter((part) => part.length > 0)
    .join("-");
  return `https://app.e-wise.nl/leraarpo/5/130/course/${courseId}/${slug}#Information`;
}

/** E-WISE course opened from “Cursus volgen”. */
export const startingTeacherCourseUrl = ewiseCourseUrl(
  "1080805",
  "Executieve functies versterken van je leerlingen",
);

export type CourseState = "open" | "done" | "skipped";
export type TopicStatus = "open" | "noted" | "self" | "mentor" | "revisit";

export type TopicProgress = {
  status: TopicStatus;
  note: string;
};

export type StartingTeacherNoteSubject = {
  kind: "competency" | "skill";
  id: string;
};

export type StartingTeacherNote = {
  id: string;
  subjectKind: StartingTeacherNoteSubject["kind"];
  subjectId: string;
  text: string;
  createdAt: string;
  /** False unless the teacher chose to share this note with their coach. */
  sharedWithCoach: boolean;
};

export type StartingTeacherProgress = {
  hasMentor: boolean;
  mentorName: string | null;
  mentorMeetingDate: string | null;
  mentorFrequency: "weekly" | "fortnightly" | "monthly" | "quarterly" | "none" | null;
  youngChild: boolean;
  specialEducation: boolean;
  activeCompetencyId: string | null;
  activeSkillId: string | null;
  activeTopicId: string | null;
  courses: Record<string, CourseState>;
  topics: Record<string, TopicProgress>;
  /** Skill id to how it reached Voltooid, or verified after the mentor signed off. */
  completedSkills: Record<string, "self" | "quiz" | "verified">;
  skillCompletedAt: Record<string, string>;
  skillVerifiedAt: Record<string, string>;
  /** ISO timestamp of the first time this skill became the active one. */
  skillStartedAt: Record<string, string>;
  /** Task ids marked behaald after Start, keyed by skill id. */
  achievedTasks: Record<string, Partial<Record<StartingTeacherTaskId, true>>>;
  /** Latest knowledge-quiz percent per skill. A perfect score stays at 100. */
  quizScores: Record<string, number>;
  /** Coach notes, each tied to a competency or skill. */
  notes: StartingTeacherNote[];
};

export type StartingTeacherTaskId = "course" | "learn" | "practice" | "chat";

export type Recommendation =
  | { kind: "course" }
  | { kind: "task" }
  | { kind: "close" }
  | { kind: "next"; topicId: string }
  | { kind: "skillDone" };

export function emptyStartingTeacherProgress(): StartingTeacherProgress {
  return {
    hasMentor: false,
    mentorName: null,
    mentorMeetingDate: null,
    mentorFrequency: null,
    youngChild: false,
    specialEducation: false,
    activeCompetencyId: null,
    activeSkillId: null,
    activeTopicId: null,
    courses: {},
    topics: {},
    completedSkills: {},
    skillCompletedAt: {},
    skillVerifiedAt: {},
    skillStartedAt: {},
    achievedTasks: {},
    quizScores: {},
    notes: [],
  };
}

/** A meeting date is past when it is before today. Today itself still counts as upcoming. */
export function mentorMeetingPassed(date: string | null, now = new Date()): boolean {
  if (!date) return false;
  const meeting = new Date(`${date}T12:00:00`);
  const today = new Date(now);
  today.setHours(12, 0, 0, 0);
  return meeting.getTime() < today.getTime();
}

export function noteSubjectValue(subject: StartingTeacherNoteSubject): string {
  return `${subject.kind}:${subject.id}`;
}

export function parseNoteSubject(value: string): StartingTeacherNoteSubject | null {
  const separator = value.indexOf(":");
  if (separator <= 0) return null;
  const kind = value.slice(0, separator);
  const id = value.slice(separator + 1);
  if (kind === "competency" && findCompetency(id)) return { kind, id };
  if (kind === "skill" && findSkill(id)) return { kind, id };
  return null;
}

/** The open skill, otherwise the open competency, otherwise the active route position. */
export function activeNoteSubject(
  progress: StartingTeacherProgress,
  location: { competencyId?: string | null; skillId?: string | null },
): StartingTeacherNoteSubject {
  if (location.skillId && findSkill(location.skillId)) return { kind: "skill", id: location.skillId };
  if (location.competencyId && findCompetency(location.competencyId)) {
    return { kind: "competency", id: location.competencyId };
  }
  if (progress.activeSkillId && findSkill(progress.activeSkillId)) {
    return { kind: "skill", id: progress.activeSkillId };
  }
  return { kind: "competency", id: highlightedCompetencyId(progress) };
}

export function topicVisible(topicRef: TopicRef, progress: StartingTeacherProgress): boolean {
  if (topicRef.audience === "young") return progress.youngChild;
  if (topicRef.audience === "special") return progress.specialEducation;
  return true;
}

export function visibleTopics(skill: SkillRef, progress: StartingTeacherProgress): TopicRef[] {
  return skill.topics.filter((entry) => topicVisible(entry, progress));
}

export function findCompetency(id: string): CompetencyRef | undefined {
  return startingTeacherRoute.find((entry) => entry.id === id);
}

export function findSkill(id: string): { competency: CompetencyRef; skill: SkillRef } | undefined {
  for (const competency of startingTeacherRoute) {
    const skill = competency.skills.find((entry) => entry.id === id);
    if (skill) return { competency, skill };
  }
  return undefined;
}

export function topicSettled(status: TopicStatus | undefined): boolean {
  return status === "self" || status === "mentor";
}

export function skillCompletion(
  skill: SkillRef,
  progress: StartingTeacherProgress,
): {
  done: number;
  total: number;
  complete: boolean;
} {
  const topics = visibleTopics(skill, progress);
  const marked = Boolean(skillMark(skill.id, progress));
  const done = topics.filter((entry) => topicSettled(progress.topics[entry.id]?.status)).length;
  const complete = marked || (topics.length > 0 && done === topics.length);
  return { done: complete ? topics.length : done, total: topics.length, complete };
}

export function competencyCompletion(
  competency: CompetencyRef,
  progress: StartingTeacherProgress,
): {
  done: number;
  total: number;
} {
  const totals = competency.skills.map((skill) => skillCompletion(skill, progress));
  return {
    done: totals.filter((entry) => entry.complete).length,
    total: competency.skills.length,
  };
}

export function routeCompletion(progress: StartingTeacherProgress): { done: number; total: number } {
  const done = startingTeacherRoute.filter((competency) => {
    const counts = competencyCompletion(competency, progress);
    return counts.total > 0 && counts.done === counts.total;
  }).length;
  return { done, total: startingTeacherRoute.length };
}

export type RouteStatus = "active" | "started" | "completed" | "verified";

function skillMark(skillId: string, progress: StartingTeacherProgress): "self" | "quiz" | "verified" | undefined {
  if (progress.skillVerifiedAt[skillId] || progress.completedSkills[skillId] === "verified") return "verified";
  const mark = progress.completedSkills[skillId];
  if (mark === "self" || mark === "quiz") return mark;
  if (progress.skillCompletedAt[skillId]) return "self";
  return undefined;
}

export function isActiveSkill(skillId: string, _competency: CompetencyRef, progress: StartingTeacherProgress): boolean {
  return progress.activeSkillId === skillId;
}

export type SkillListAction = "start" | "continue" | "jump";

/** Every skill can be started until one in this competency is active. */
export function skillListAction(
  skillId: string,
  competency: CompetencyRef,
  progress: StartingTeacherProgress,
): SkillListAction {
  if (progress.activeSkillId === skillId) return "continue";
  if (competency.skills.some((skill) => skill.id === progress.activeSkillId)) return "jump";
  return "start";
}

/** Voltooid from a self mark or a perfect quiz. Geverifieerd replaces it after the mentor signs off. */
export function skillStatus(skill: SkillRef, progress: StartingTeacherProgress, active: boolean): RouteStatus | null {
  const mark = skillMark(skill.id, progress);
  if (mark === "verified") return "verified";
  if (mark === "self" || mark === "quiz") return "completed";
  return active ? "active" : null;
}

export function competencyHasProgress(competency: CompetencyRef, progress: StartingTeacherProgress): boolean {
  return competency.skills.some((skill) => {
    if (skillMark(skill.id, progress)) return true;
    if (progress.activeSkillId === skill.id) return true;
    const course = progress.courses[skill.id];
    if (course && course !== "open") return true;
    return skill.topics.some((topic) => {
      const state = progress.topics[topic.id];
      return Boolean(state && (state.note.trim() || state.status !== "open"));
    });
  });
}

export function competencyStatus(
  competency: CompetencyRef,
  progress: StartingTeacherProgress,
  active: boolean,
): RouteStatus | null {
  const marks = competency.skills.map((skill) => skillMark(skill.id, progress));
  if (marks.length > 0 && marks.every((mark) => mark === "verified")) return "verified";
  if (marks.length > 0 && marks.every((mark) => mark === "self" || mark === "quiz" || mark === "verified")) {
    return "completed";
  }
  if (active) return "active";
  return competencyHasProgress(competency, progress) ? "started" : null;
}

export function highlightedCompetencyId(progress: StartingTeacherProgress): string {
  return progress.activeCompetencyId ?? routeStart.competencyId;
}

function courseReady(progress: StartingTeacherProgress, skillId: string): boolean {
  const state = progress.courses[skillId] ?? "open";
  return state === "done" || state === "skipped";
}

export function recommend(
  skill: SkillRef,
  progress: StartingTeacherProgress,
  topicId: string,
): Recommendation {
  if (!courseReady(progress, skill.id)) return { kind: "course" };
  const current = progress.topics[topicId];
  const status = current?.status ?? "open";
  if (!topicSettled(status)) {
    return current?.note.trim() ? { kind: "close" } : { kind: "task" };
  }
  const topics = visibleTopics(skill, progress);
  const index = topics.findIndex((entry) => entry.id === topicId);
  const next = topics[index + 1];
  return next ? { kind: "next", topicId: next.id } : { kind: "skillDone" };
}

export function furtherLearning(recommendation: Recommendation): Array<"practice" | "learn" | "chat"> {
  if (recommendation.kind === "course") return ["practice", "learn", "chat"];
  return ["learn", "chat"];
}
